// Frontend/src/hooks/useInvitations.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  sendBulkInvitations,
  resendInvitation,
  revokeInvitation,
  checkInvitationStatus,
  listInvitations,
  requestOTP,
  verifyOTP,
  checkPasswordChangeStatus,
  changePassword,
  type BulkInviteRequest,
  type BulkInviteResponse,
  type InvitationStatusCheck,
  type InvitationStatusResponse,
  type ResidentInvitation,
  type OTPRequest,
  type OTPRequestResponse,
  type OTPVerify,
  type OTPVerifyResponse,
  type PasswordChangeRequest,
  type PasswordChangeResponse,
  type PasswordChangeStatusResponse,
} from "@/api/invitations";

/**
 * Query key factory for invitations
 */
export const invitationKeys = {
  all: ["invitations"] as const,
  lists: () => [...invitationKeys.all, "list"] as const,
  list: () => [...invitationKeys.lists()] as const,
  status: (code: string, email: string) => 
    [...invitationKeys.all, "status", code, email] as const,
  passwordStatus: () => ["password", "status"] as const,
};

/**
 * Fetch all invitations (for admin)
 */
export function useInvitations() {
  return useQuery<ResidentInvitation[], Error>({
    queryKey: invitationKeys.list(),
    queryFn: listInvitations,
  });
}

/**
 * Send bulk invitations
 */
export function useSendBulkInvitations() {
  const queryClient = useQueryClient();

  return useMutation<BulkInviteResponse, Error, BulkInviteRequest>({
    mutationFn: sendBulkInvitations,
    onSuccess: () => {
      // Invalidate invitations list to refetch
      queryClient.invalidateQueries({ queryKey: invitationKeys.lists() });
    },
  });
}

/**
 * Resend invitation
 */
export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; message: string }, Error, string>({
    mutationFn: resendInvitation,
    onSuccess: () => {
      // Invalidate invitations list
      queryClient.invalidateQueries({ queryKey: invitationKeys.lists() });
    },
  });
}

/**
 * Revoke invitation
 */
export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; message: string }, Error, string>({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      // Invalidate invitations list
      queryClient.invalidateQueries({ queryKey: invitationKeys.lists() });
    },
  });
}

/**
 * Check invitation status (public - no auth required)
 */
export function useCheckInvitationStatus(code: string, email: string) {
  return useQuery<InvitationStatusResponse, Error>({
    queryKey: invitationKeys.status(code, email),
    queryFn: () => checkInvitationStatus({ invitation_code: code, email }),
    enabled: !!code && !!email,
  });
}

/**
 * OTP Hooks
 */

/**
 * Request OTP code
 */
export function useRequestOTP() {
  return useMutation<OTPRequestResponse, Error, OTPRequest>({
    mutationFn: requestOTP,
  });
}

/**
 * Verify OTP code
 */
export function useVerifyOTP() {
  return useMutation<OTPVerifyResponse, Error, OTPVerify>({
    mutationFn: verifyOTP,
  });
}

/**
 * Password Change Hooks
 */

/**
 * Check if password change is required
 */
export function usePasswordChangeStatus() {
  return useQuery<PasswordChangeStatusResponse, Error>({
    queryKey: invitationKeys.passwordStatus(),
    queryFn: checkPasswordChangeStatus,
  });
}

/**
 * Change password
 */
export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation<PasswordChangeResponse, Error, PasswordChangeRequest>({
    mutationFn: changePassword,
    onSuccess: () => {
      // Invalidate password status
      queryClient.invalidateQueries({ queryKey: invitationKeys.passwordStatus() });
    },
  });
}
